// Daten-Kontrakt v4 (Viewer-Seite).
// ACHTUNG: bewusst dupliziert im Backend (src/template/contract/contract-v1.ts)
// und im SDK (vcard-template-sdk/sdk/src/types.ts) — Änderungen an allen drei Stellen.
//
// v4 (11.09.2026) ersetzt `vcard.team` (ein Objekt) durch `vcard.locations` (eine
// Liste) und benennt `appointmentOnly` in `byAppointment` um; `vcard.appointmentOnly`
// an der Person entfaellt ersatzlos.
//
// 🔴 v4 ist NICHT additiv, und deshalb faellt 1-3 hier heraus. Ein Bestandstemplate,
// das `vcard.team` liest, bekaeme sonst `undefined` und rendert stillschweigend
// keinen Standort — eine halb funktionierende Karte, die niemandem auffaellt. Die
// sichtbare Ablehnung (Rueckfall auf ClassicTemplate) ist die ehrlichere Antwort.

// v5 (13.09.2026) ergänzt markeneigene Zusatzfelder (Spec §2–§5, T-06):
// `vcard.customFields` und `ContextLocation.customFields`, je Position
// aufgelöst über `resolveCustomFields`. Additiv — ein Bestandstemplate, das
// die Felder nicht kennt, läuft unverändert weiter, deshalb bleibt 4 in
// SUPPORTED_CONTRACT_VERSIONS.

// v6 (15.09.2026, C3) — letzter Schritt vor der Bilder-Objektspeicher-Migration:
// fünf fachliche Felder (`vcard.instagram`, `vcard.provenExpert`,
// `vcard.employeeId`, `vcard.companyId`, `ContextLocation.locationId`) werden
// erstmals im Kontrakt sichtbar, und Marken-/Standortlogo werden eigene
// Adressen statt `data:`-URIs (siehe `profileImage` oben — derselbe Hebel).
// Additiv — ein Bestandstemplate, das die neuen Felder nicht kennt, läuft
// unverändert weiter, deshalb bleiben 4 und 5 in SUPPORTED_CONTRACT_VERSIONS.

// v8 (01.10.2026): additiv — `vcard.companyName` (Firma der Person). Bestands-
// templates v4–v7 laufen unverändert weiter und sehen das Feld nur nicht.
import type { ReactElement } from 'react';

export const CONTRACT_VERSION = 8;
export const SUPPORTED_CONTRACT_VERSIONS = [4, 5, 6, 7, 8];

/** Positionstags am vCard-Träger selbst (Person). */
export const VCARD_POSITION_TAGS = [
  'headline',
  'contact',
  'profile',
  'footer',
] as const;

/** Positionstags an einem Standort (`vcard.locations[n]`). */
export const LOCATION_POSITION_TAGS = [
  'locationHeadline',
  'locationDetails',
  'locationFooter',
] as const;

export const POSITION_TAGS = [
  ...VCARD_POSITION_TAGS,
  ...LOCATION_POSITION_TAGS,
] as const;

export type PositionTag = (typeof POSITION_TAGS)[number];

export const CUSTOM_FIELD_TYPES = [
  'text',
  'link',
  'number',
  'date',
  'select',
] as const;

export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/**
 * Traeger eines Zusatzfeldes.
 *
 * Diese und die Schluesselform darunter stehen im SDK bewusst NICHT: ein
 * Template bekommt fertige Eintraege je Position und prueft weder Traeger noch
 * Schluesselform. Im Backend (`template/contract/custom-fields.ts`) und hier
 * gehoeren sie zum Kontrakt, weil Schema, DTO und die Konsole daran haengen —
 * und eine Kopie „daneben" in der Konsole driftet ohne Compilerfehler ab.
 */
export const CUSTOM_FIELD_TARGETS = ['vcard', 'location'] as const;

export type CustomFieldTarget = (typeof CUSTOM_FIELD_TARGETS)[number];

/**
 * Schluesselform wie ueberall im Kontrakt: kein Punkt, kein `$`, keine
 * Grossbuchstaben — Mongo lehnt `.`/`$` in Schluesseln ab, und ein
 * case-insensitiver Schluessel waere eine zweite Normalisierungsstelle.
 */
export const CUSTOM_FIELD_KEY = /^[a-z][a-z0-9_]{0,39}$/;

/** Fertiger Eintrag fuer eine Template-Position (Spec §5). */
export interface CustomFieldEntry {
  key: string;
  label: string;
  type: CustomFieldType;
  /** ISO bei `date`, sonst wie eingegeben. */
  value: string;
  /** Fertig zur Anzeige — bei `date` lokalisiert (de-DE, UTC), sonst = value. */
  display: string;
}

/** Zusatzfelder je Position, wie sie ein Template erhaelt. */
export type CustomFieldsByPosition = Partial<
  Record<PositionTag, CustomFieldEntry[]>
>;

export interface DayTime {
  day: number; // 1 = Montag … 7 = Sonntag (days.dto.ts: @Min(1) @Max(7))
  time: string; // HHMM, z. B. "0900"
}

export interface ContextOpeningHour {
  open: DayTime;
  close: DayTime;
}

export interface ContextLocation {
  name?: string;
  logo?: string;
  address?: Record<string, unknown>;
  openingHours?: ContextOpeningHour[];
  /**
   * Dieser Standort arbeitet (auch) nach Vereinbarung — **zusaetzlich** zu den
   * Oeffnungszeiten oder allein. Hiess bis v3 `appointmentOnly`; „only" behauptete
   * eine Ausschliesslichkeit, die es nie gab.
   */
  byAppointment?: boolean;
  /** Fachliche Standortnummer (C1: Datenmodell, C3: erstmals im Kontrakt
   *  sichtbar) — kein eigener Allowlist-Pfad, haengt an `vcard.locations[n]`
   *  mit. */
  locationId?: string;
  /** Zusatzfelder der Marke, ausgewertet je Position (Spec §5). Nur gesetzt,
   *  wenn mindestens ein Feld belegt ist — kein leeres Objekt. */
  customFields?: CustomFieldsByPosition;
}

export interface VcardContextData {
  vcard: {
    firstName?: string;
    lastName?: string;
    role?: string;
    /**
     * Bild-URL, nicht die Bilddaten.
     *
     * Bis 11.08.2026 lieferte der Kontrakt hier eine `data:`-URI. Das
     * Kontext-JSON war dadurch 41 KB gross und bestand zu 85 % aus dem Bild —
     * bei JEDEM Aufruf neu uebertragen. Jetzt steht hier eine URL mit
     * Inhalts-Fingerabdruck (`/vcard/image/<urlKey>?v=<hash>`), die
     * unveraenderlich gecacht wird; der Folgeaufruf faellt auf ~1,5 KB.
     *
     * Fuer `<img src>` ist das gleichwertig — der Typ bleibt `string`, die
     * `contractVersion`-Prüfung der Bundle-Validierung bleibt unberührt.
     * Wer die BYTES braucht (z. B. fuer eine .vcf-Datei), muss die URL
     * abrufen.
     *
     * Seit v6 gilt derselbe Hebel fuer `brand.logo`/`ContextLocation.logo`
     * (Adresse statt `data:`-URI). `brand.signet` blieb damals ausgenommen und
     * entfaellt seit v7 ganz — 84 % der gemessenen Kontext-Antwort fuer ein
     * Bild, das kein Template rendert.
     */
    profileImage?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    whatsapp?: string;
    facebook?: string;
    linkedin?: string;
    website?: string;
    /** Volle URL (C1: Datenmodell, C3: erstmals im Kontrakt sichtbar). */
    instagram?: string;
    /** Volle URL (C1: Datenmodell, C3: erstmals im Kontrakt sichtbar). */
    provenExpert?: string;
    /** Fachliche Mitarbeiternummer (C1: Datenmodell, C3: erstmals im
     *  Kontrakt sichtbar). */
    employeeId?: string;
    /** Fachliche Firmennummer (C1: Datenmodell, C3: erstmals im Kontrakt
     *  sichtbar). */
    companyId?: string;
    /** Firma der Person (v8, 01.10.2026). Templates entscheiden selbst über
     *  die Anzeige; für ORG im .vcf gilt companyName → brand.name →
     *  locations[0].name. */
    companyName?: string;
    /**
     * Die Orte, an denen die Person erreichbar ist. **`locations[0]` ist der
     * Hauptstandort** — Karte, strukturierte Daten und `.vcf`-Export nehmen ihn.
     * Ein Template, das nur einen Ort zeigen will, nimmt den ersten.
     */
    locations?: ContextLocation[];
    phoneDisplay?: string;
    mobileDisplay?: string;
    /** Zusatzfelder der Marke, ausgewertet je Position (Spec §5). Nur gesetzt,
     *  wenn mindestens ein Feld belegt ist — kein leeres Objekt. */
    customFields?: CustomFieldsByPosition;
  };
  brand: {
    name?: string;
    primaryColor?: string;
    logo?: string;
    claim?: string;
    notFoundRedirectUrl?: string;
  };
}

/**
 * Kontext für Ansichten ohne vCard (404, 410, Startseite): nur `brand.*`.
 * Bewusst kein `vcard`-Zweig — es gibt in diesen Fällen keine vCard.
 */
export interface SiteContextData {
  brand: VcardContextData['brand'];
}

/** Welche Ansicht das Template rendern soll. `view` und `kind` heißen bewusst
 *  gleich — keine zweite Bezeichnung für dieselbe Sache (keine 'error'-Variante). */
/**
 * Die Allowlist-Pfade des Kontrakts — wortgleich mit `CONTRACT_FIELDS` in
 * `backend/src/template/contract/contract-v1.ts`.
 *
 * Steht hier, weil die Konsole sie braucht (Marken-Allowlist) und eine
 * **eigene Kopie daneben** ohne Compilerfehler abdriftet: Genau das ist
 * passiert — `BrandEditorView` fuehrte eine handgepflegte Liste, der nach dem
 * Kontraktsprung auf v6 fuenf Pfade fehlten. Eine Marke, die ihre Allowlist
 * einschraenkt, haette die neuen Felder damit weder im Viewer gesehen noch in
 * der Konsole einschalten koennen. Dieselbe Begruendung wie bei
 * `POSITION_LABELS` (siehe CLAUDE.md).
 */
export const CONTRACT_FIELDS = [
  'vcard.firstName',
  'vcard.lastName',
  'vcard.role',
  'vcard.profileImage',
  'vcard.email',
  'vcard.phone',
  'vcard.mobile',
  'vcard.whatsapp',
  'vcard.facebook',
  'vcard.linkedin',
  'vcard.website',
  'vcard.instagram',
  'vcard.provenExpert',
  'vcard.employeeId',
  'vcard.companyId',
  'vcard.companyName',
  'vcard.locations',
  'vcard.phoneDisplay',
  'vcard.mobileDisplay',
  'vcard.customFields',
  'brand.name',
  'brand.primaryColor',
  'brand.logo',
  'brand.claim',
  'brand.notFoundRedirectUrl',
] as const;

export type ContractField = (typeof CONTRACT_FIELDS)[number];

export type ViewKind = 'vcard' | 'notFound' | 'gone' | 'landing';

export interface TemplateRef {
  id: string;
  name: string;
  version: string;
  contractVersion: number;
  capabilities: string[];
  bundleUrl: string;
  stylesUrl?: string;
  assetBaseUrl: string;
  preview?: string;
}

export interface VcardViewPayload {
  kind?: 'vcard';
  contractVersion: number;
  context: VcardContextData;
  template: TemplateRef | null;
}

/** Der Slug bleibt hier erlaubt — anders als bei `GonePayload` wurde nichts
 *  gelöscht, es gibt schlicht (noch) keine vCard unter dieser Adresse. */
export interface NotFoundPayload {
  kind: 'notFound';
  contractVersion: number;
  context: SiteContextData;
  template: TemplateRef | null;
  urlKey?: string;
}

/** Bewusst KEIN `urlKey`: der Slug einer anonymisierten Person ist selbst ein
 *  Personenbezug — deshalb liegt dessen SHA-256 in der Sperrliste, nicht der
 *  Klartext. */
export interface GonePayload {
  kind: 'gone';
  contractVersion: number;
  context: SiteContextData;
  template: TemplateRef | null;
}

export interface LandingPayload {
  kind: 'landing';
  contractVersion: number;
  context: SiteContextData;
  template: TemplateRef | null;
}

export type ViewPayload =
  | VcardViewPayload
  | NotFoundPayload
  | GonePayload
  | LandingPayload;

/**
 * Ereignisarten, die ein Template per `onEvent` melden darf (Kontrakt v3).
 * Bewusst eine feste Aufzählung, kein freier String — sonst waere der
 * Rueckruf eine Einladung, personenbezogene Daten in die Statistik zu
 * schreiben (die Zaehlung ist ausdruecklich ohne Personenbezug gebaut, siehe
 * VcardStatsService). Muss Wort fuer Wort mit den `click:*`-Eintraegen aus
 * `STAT_EVENTS` (Backend: `src/vcard/lifecycle.config.ts`) uebereinstimmen —
 * dort steht die serverseitige Allowlist, hier nur die dem Template
 * sichtbare Teilmenge (ohne `view`: Aufrufe zaehlt der Server selbst beim
 * Ausliefern des Kontexts, siehe `stats.controller.ts`-Kommentar).
 */
export type TemplateStatEvent =
  | 'click:email'
  | 'click:phone'
  | 'click:mobile'
  | 'click:whatsapp'
  | 'click:facebook'
  | 'click:linkedin'
  | 'click:google-business'
  | 'click:website'
  | 'click:save-contact'
  | 'click:navigation';

export interface TemplateProps {
  data: VcardContextData;
  asset: (name: string) => string;
  /**
   * Kontrakt v3, additiv: meldet eine Kontakt-Betätigung für die
   * Nutzungsstatistik der Konsole. Optional — ein Template, das dieses Feld
   * nicht kennt oder nicht aufruft, funktioniert unverändert
   * (Rückwärtskompatibilität zu v1/v2). Feuert-und-vergisst: darf nie werfen
   * und darf den eigentlichen Klick (tel:/mailto:/wa.me/…) niemals verzögern.
   */
  onEvent?: (event: TemplateStatEvent) => void;
}

export type TemplateComponent = (props: TemplateProps) => ReactElement | null;

export interface ErrorProps {
  data: SiteContextData;
  asset: (name: string) => string;
  error: { kind: 'notFound'; urlKey?: string };
}

export interface GoneProps {
  data: SiteContextData;
  asset: (name: string) => string;
}

export interface LandingProps {
  data: SiteContextData;
  asset: (name: string) => string;
}

export type ErrorViewComponent = (props: ErrorProps) => ReactElement | null;
export type GoneViewComponent = (props: GoneProps) => ReactElement | null;
export type LandingViewComponent = (props: LandingProps) => ReactElement | null;
